import {useMemo} from 'react';
import {useUncontrolled} from './controlled.hook';

export const DOTS = 'dots';

export interface PaginationParams {
  initialOffset?: number;
  offset?: number;
  total: number;
  limit: number;
  siblings?: number;
  boundaries?: number;
  onChange?: (offset: number) => void;
}

export function usePagination({
  total,
  limit,
  siblings = 1,
  boundaries = 1,
  offset,
  initialOffset = 0,
  onChange,
}: PaginationParams) {
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const [activeOffset, setActiveOffset] = useUncontrolled({
    value: offset,
    onChange,
    defaultValue: initialOffset,
    finalValue: initialOffset,
  });

  const setOffset = (newOffset: number) => {
    const clampedOffset = Math.max(0, Math.min(newOffset, (totalPages - 1) * limit));
    setActiveOffset(clampedOffset);
  };

  const next = () => setOffset(activeOffset + limit);
  const previous = () => setOffset(activeOffset - limit);
  const first = () => setOffset(0);
  const last = () => setOffset((totalPages - 1) * limit);

  const paginationRange = useMemo((): (number | 'dots')[] => {
    const currentPage = activeOffset / limit + 1;
    const totalPageNumbers = siblings * 2 + 3 + boundaries * 2;

    if (totalPageNumbers >= totalPages) {
      return range(0, totalPages - 1).map((p) => p * limit);
    }

    const leftSiblingIndex = Math.max(currentPage - siblings, boundaries + 1);
    const rightSiblingIndex = Math.min(currentPage + siblings, totalPages - boundaries);

    const shouldShowLeftDots = leftSiblingIndex > boundaries + 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - (boundaries + 1);

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = siblings * 2 + boundaries + 2;
      return [
        ...range(0, leftItemCount - 1).map((p) => p * limit),
        DOTS,
        ...range(totalPages - boundaries, totalPages - 1).map((p) => p * limit),
      ];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = boundaries + 1 + 2 * siblings;
      return [
        ...range(0, boundaries - 1).map((p) => p * limit),
        DOTS,
        ...range(totalPages - rightItemCount, totalPages - 1).map((p) => p * limit),
      ];
    }

    return [
      ...range(0, boundaries - 1).map((p) => p * limit),
      DOTS,
      ...range(leftSiblingIndex - 1, rightSiblingIndex - 1).map((p) => p * limit),
      DOTS,
      ...range(totalPages - boundaries, totalPages - 1).map((p) => p * limit),
    ];
  }, [totalPages, siblings, activeOffset, limit]);

  return {
    range: paginationRange,
    active: activeOffset,
    setOffset,
    next,
    previous,
    first,
    last,
  };
}

function range(start: number, end: number) {
  return Array.from({length: end - start + 1}, (_, index) => index + start);
}
